import { ChartAreaInteractive } from '@/components/chart-area-interactive'
import { SectionCards } from './SectionCards'
import { DashboardActions, type DashboardSummaryMode } from './DashboardActions'
import { useGetDashboard } from '@/modules/dashboard/dashboard'
import { useProfileData } from '@/hooks/useProfile'
import { useMemo, useState } from 'react'
import { Skeleton as BoneyardSkeleton } from 'boneyard-js/react'
import { Skeleton as UiSkeleton } from '@/components/ui/skeleton'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useInitialLocationFilter } from '@/hooks/useInitialLocationFilter'

dayjs.extend(utc)
dayjs.extend(timezone)

const BKK = 'Asia/Bangkok'

export const OverallDashboard = () => {
  const [summaryMode, setSummaryMode] = useState<DashboardSummaryMode>('today')
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(BKK).startOf('month').format('YYYY-MM-DD'))
  const [dateTo, setDateTo] = useState(() => dayjs().tz(BKK).format('YYYY-MM-DD'))
  const [summaryYear, setSummaryYear] = useState(() => dayjs().tz(BKK).year())
  const [reportLocation, setReportLocation] = useInitialLocationFilter()

  const dashboardParams = useMemo(() => {
    const location = reportLocation || undefined
    if (summaryMode === 'today') {
      const today = dayjs().tz(BKK).format('YYYY-MM-DD')
      return { dateFrom: today, dateTo: today, location }
    }
    if (summaryMode === 'all') {
      return { scope: 'all' as const, location }
    }
    if (summaryMode === 'year') {
      return { year: String(summaryYear), location }
    }
    if (summaryMode === 'range') {
      const ymd = /^\d{4}-\d{2}-\d{2}$/
      const n = dayjs().tz(BKK)
      const from =
        dateFrom && ymd.test(dateFrom) ? dateFrom : n.startOf('month').format('YYYY-MM-DD')
      const to = dateTo && ymd.test(dateTo) ? dateTo : n.format('YYYY-MM-DD')
      return { dateFrom: from, dateTo: to, location }
    }
    return {
      dateFrom: dayjs().tz(BKK).startOf('month').format('YYYY-MM-DD'),
      dateTo: dayjs().tz(BKK).format('YYYY-MM-DD'),
      location,
    }
  }, [summaryMode, dateFrom, dateTo, summaryYear, reportLocation])

  const { data: dashboard, isLoading: isDashboardLoading } = useGetDashboard(dashboardParams)
  const { checkPermission } = useProfileData()

  return (
    <div className="flex flex-1 flex-col w-full h-full">
      <div className="flex flex-1 flex-col gap-2">
        {checkPermission('admin_view_calculate_bill') ? (
          <div className="flex flex-col gap-6 py-4 md:py-6">
            <BoneyardSkeleton
              name="admin-overall-dashboard-content"
              loading={isDashboardLoading}
              fallback={(
                <div className="flex flex-col gap-6">
                  <UiSkeleton className="h-14 w-full rounded-2xl" />
                  <div className="w-full overflow-x-auto pb-2">
                    <div className="flex min-w-max gap-4">
                      <UiSkeleton className="h-40 w-[260px] shrink-0 rounded-2xl" />
                      <UiSkeleton className="h-40 w-[260px] shrink-0 rounded-2xl" />
                      <UiSkeleton className="h-40 w-[260px] shrink-0 rounded-2xl" />
                      <UiSkeleton className="h-40 w-[300px] shrink-0 rounded-2xl" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full">
                    <UiSkeleton className="h-[600px] w-full rounded-2xl" />
                    <UiSkeleton className="h-[600px] w-full rounded-2xl" />
                  </div>
                </div>
              )}
            >
              <DashboardActions
                summaryMode={summaryMode}
                onSummaryModeChange={setSummaryMode}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateRangeChange={(from, to) => {
                  setDateFrom(from)
                  setDateTo(to)
                }}
                summaryYear={summaryYear}
                onSummaryYearChange={setSummaryYear}
                reportLocation={reportLocation}
                onReportLocationChange={setReportLocation}
              />

              <SectionCards data={dashboard?.data} />

              <div className="mt-4 xl:grid-cols-2 gap-6 w-full items-stretch min-w-0">
                <div className="w-full min-w-0">
                  <ChartAreaInteractive
                    reportLocation={reportLocation || undefined}
                  />
                </div>
              </div>
            </BoneyardSkeleton>
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-4 md:py-6">
            <div className="text-2xl font-bold">ไม่มีสิทธิ์เข้าถึง</div>
          </div>
        )}
      </div>
    </div>
  )
}
